import React from "react";
import ModalShell from "./ModalShell";
import Field from "./Field";
import { useBreaksCampaign } from "../state/BreaksCampaignContext";
import { useSchoolSearch } from "../hooks/useSchoolSearch";
import Button from "./Shared/Button";
import ProgressBar from "./Shared/ProgressBar";
import ModalStep from "./Shared/ModalStep";
import { RegistrationError } from "../api/registrations";

export interface SignupForm {
  firstName: string;
  lastName: string;
  email: string;
  schoolQuery: string;
  school: string;
  /** Skolverkets skolenhetskod. Det är den som skickas med anmälan, inte namnet. */
  schoolUnitCode: string;
  municipality: string;
  className: string;
  students: string;
  /** Honungsfälla. Dold för människor, så ett värde här betyder en bot. */
  website: string;
}

interface SignupModalProps {
  onClose: () => void;
}

type SignupStep = 1 | 2 | 3 | 4;

type FieldElement = HTMLInputElement | HTMLSelectElement;

const EMPTY: SignupForm = {
  firstName: "",
  lastName: "",
  email: "",
  schoolQuery: "",
  school: "",
  schoolUnitCode: "",
  municipality: "",
  className: "",
  students: "",
  website: "",
};

const isEmail = (value: string): boolean => /\S+@\S+\.\S+/.test(value);

export function SignupModal({ onClose }: SignupModalProps) {
  const { registerClass, registration } = useBreaksCampaign();
  const [step, setStep] = React.useState<SignupStep>(registration ? 4 : 1);
  const [form, setForm] = React.useState<SignupForm>(EMPTY);
  const [touched, setTouched] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [duplicateWarning, setDuplicateWarning] = React.useState(false);

  const students = Number.parseInt(form.students, 10) || 0;
  const setValue =
    (key: keyof SignupForm) =>
    (event: React.ChangeEvent<FieldElement>): void => {
      setForm((prev) => ({ ...prev, [key]: event.target.value }));
    };

  const errors = {
    firstName: form.firstName.trim() === "",
    email: !isEmail(form.email),
    // Skolan måste vara vald ur träfflistan. Fritext går inte att koppla till registret.
    school: form.schoolUnitCode === "",
    className: form.className.trim() === "",
    students: students < 1 || students > 100,
  };
  const stepValid =
    step === 1
      ? !errors.firstName && !errors.email
      : step === 2
        ? !errors.school && !errors.className && !errors.students
        : true;

  // Sökningen pausas så fort en skola valts, annars skulle valet trigga en ny sökning
  // på det ifyllda namnet och öppna listan igen.
  const {
    hits,
    loading: searching,
    failed: searchFailed,
  } = useSchoolSearch(
    form.schoolQuery,
    step === 2 && form.schoolUnitCode === "",
  );

  const goNext = (): void => {
    if (!stepValid) {
      setTouched(true);
      return;
    }
    setTouched(false);
    setStep((prev) => (prev + 1) as SignupStep);
  };

  // Enter i e-postfältet ska göra samma sak som Nästa-knappen. Fälten ligger inte i ett
  // <form>, så webbläsaren skickar inget av sig själv.
  const goNextOnEnter = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key !== "Enter") return;

    event.preventDefault();
    goNext();
  };

  const submit = async (confirmDuplicate = false): Promise<void> => {
    // Dubbelklick på knappen skulle annars skapa två anmälningar.
    if (submitting) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      await registerClass(form, confirmDuplicate);
      setDuplicateWarning(false);
      setStep(4);
    } catch (error: unknown) {
      if (error instanceof RegistrationError && error.possibleDuplicate) {
        setDuplicateWarning(true);
        return;
      }

      setSubmitError(
        error instanceof Error ? error.message : "Anmälan kunde inte sparas.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const backToClass = (): void => {
    setDuplicateWarning(false);
    setSubmitError(null);
    setStep(2);
  };

  const link = registration?.link ?? "";
  const videoLink = registration?.videoLink ?? "";

  const [copied, setCopied] = React.useState(false);
  const copy = (): void => {
    if (link === "") return;

    navigator.clipboard?.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <ModalShell onClose={onClose} dialogClassName="signup-modal__dialog">
      <div className="modal-content signup-modal__content">
        <div className="signup-modal__head">
          <p className="breaks-eyebrow mb-0">
            {step === 4 ? "Klart" : "Steg " + step + " av 3"}
          </p>
          <button
            type="button"
            className="signup-modal__close"
            onClick={onClose}
            aria-label="Stäng anmälan"
          >
            &times;
          </button>
        </div>

        {step < 4 && (
          <ProgressBar
            min={1}
            max={3}
            progress={step}
            label="Framsteg i anmälan"
          />
        )}

        {step === 1 && (
          <ModalStep title="Vem anmäler?">
            <div className="row g-3">
              <div className="col-sm-6">
                <Field
                  id="firstName"
                  label="Förnamn"
                  invalid={touched && errors.firstName}
                >
                  <input
                    id="firstName"
                    className="breaks-field__input"
                    value={form.firstName}
                    onChange={setValue("firstName")}
                    maxLength={100}
                    autoComplete="given-name"
                    aria-required="true"
                    aria-invalid={touched && errors.firstName}
                  />
                </Field>
              </div>
              <div className="col-sm-6">
                <Field id="lastName" label="Efternamn">
                  <input
                    id="lastName"
                    className="breaks-field__input"
                    value={form.lastName}
                    onChange={setValue("lastName")}
                    maxLength={100}
                    autoComplete="family-name"
                  />
                </Field>
              </div>
            </div>
            <Field
              id="email"
              label="E-postadress"
              invalid={touched && errors.email}
              hint={
                touched && errors.email
                  ? "Ange en giltig e-postadress."
                  : "Hit skickas er lärarlänk. Den behövs för att bekräfta anmälan och rapportera."
              }
            >
              <input
                id="email"
                type="email"
                className="breaks-field__input"
                value={form.email}
                maxLength={254}
                onChange={setValue("email")}
                onKeyDown={goNextOnEnter}
                placeholder="namn@skola.se"
                aria-describedby="email-hint"
                aria-required="true"
                aria-invalid={touched && errors.email}
                autoComplete="email"
              />
            </Field>
            {/* Honungsfälla för bottar. Dold för både seende och skärmläsare. */}
            <div className="visually-hidden" aria-hidden="true">
              <label htmlFor="signup-website">Lämna tomt</label>
              <input
                id="signup-website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={setValue("website")}
              />
            </div>
            <div className="signup-modal__actions">
              <p className="breaks-field__hint mb-0">
                Uppgifterna används bara för Breaks.
              </p>
              <Button onClick={goNext}>Nästa</Button>
            </div>
          </ModalStep>
        )}

        {step === 2 && (
          <ModalStep title="Skola och klass">
            <Field
              id="school"
              label="Skola"
              invalid={touched && errors.school}
              hint={
                touched && errors.school
                  ? "Välj en skola ur listan."
                  : searchFailed
                    ? "Sökningen kunde inte genomföras. Försök igen."
                    : "Listan kommer från Skolverkets register."
              }
            >
              <div className="breaks-field__combobox">
                <input
                  id="school"
                  className="breaks-field__input"
                  value={form.schoolQuery}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setForm((prev) => ({
                      ...prev,
                      schoolQuery: event.target.value,
                      school: "",
                      schoolUnitCode: "",
                      municipality: "",
                    }))
                  }
                  placeholder="Sök på skolans namn"
                  aria-describedby="school-hint"
                  aria-required="true"
                  aria-invalid={touched && errors.school}
                  aria-busy={searching}
                  autoComplete="off"
                />
                {hits.length > 0 && (
                  <ul className="breaks-field__results" id="school-results">
                    {hits.map((school) => (
                      <li key={school.schoolUnitCode}>
                        <button
                          type="button"
                          className="breaks-field__result"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              school: school.name,
                              schoolUnitCode: school.schoolUnitCode,
                              municipality: school.municipalityName ?? "",
                              schoolQuery: school.name,
                            }))
                          }
                        >
                          {school.displayName}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <span
                className="visually-hidden"
                role="status"
                aria-live="polite"
              >
                {searching
                  ? "Söker skolor."
                  : hits.length > 0
                    ? `${hits.length} skolor hittades.`
                    : ""}
              </span>
            </Field>
            <div className="row g-3">
              <div className="col-sm-6">
                <Field
                  id="municipality"
                  label="Kommun"
                  hint={
                    form.schoolUnitCode !== "" && form.municipality === ""
                      ? "Kommun saknas för den valda skolan."
                      : undefined
                  }
                >
                  <input
                    id="municipality"
                    className="breaks-field__input breaks-field__input--readonly"
                    value={form.municipality}
                    placeholder={
                      form.schoolUnitCode === ""
                        ? "Fylls i när du valt skola"
                        : "—"
                    }
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                  />
                </Field>
              </div>
              <div className="col-sm-6">
                <Field
                  id="className"
                  label="Klass eller grupp"
                  invalid={touched && errors.className}
                  hint={
                    touched && errors.className
                      ? "Ange ett namn för klassen."
                      : ""
                  }
                >
                  <input
                    id="className"
                    className="breaks-field__input"
                    value={form.className}
                    onChange={setValue("className")}
                    maxLength={50}
                    placeholder="t.ex. 5B"
                    aria-required="true"
                    aria-invalid={touched && errors.className}
                    aria-describedby={
                      touched && errors.className ? "className-hint" : undefined
                    }
                  />
                </Field>
              </div>
            </div>
            <Field
              id="students"
              label="Antal elever"
              invalid={touched && errors.students}
              hint={
                touched && errors.students
                  ? students < 1
                    ? "Ange minst 1 elev."
                    : "Ange en siffra mindre än 100"
                  : ""
              }
            >
              <input
                id="students"
                type="number"
                min={1}
                className="breaks-field__input breaks-field__input--narrow"
                value={form.students}
                onChange={setValue("students")}
                aria-required="true"
                aria-invalid={touched && errors.students}
                aria-describedby={
                  touched && errors.students ? "students-hint" : undefined
                }
              />
            </Field>
            <div className="signup-modal__actions">
              <Button variant="link" onClick={() => setStep(1)}>
                Tillbaka
              </Button>
              <Button onClick={goNext}>Nästa</Button>
            </div>
          </ModalStep>
        )}

        {step === 3 && (
          <ModalStep title="Stämmer det här?">
            <dl className="signup-modal__summary mb-0">
              <div className="signup-modal__summary-row">
                <dt>Kontakt</dt>
                <dd>{(form.firstName + " " + form.lastName).trim()}</dd>
              </div>
              <div className="signup-modal__summary-row">
                <dt>E-post</dt>
                <dd>{form.email}</dd>
              </div>
              <div className="signup-modal__summary-row">
                <dt>Skola</dt>
                <dd>
                  {form.school}, {form.municipality}
                </dd>
              </div>
              <div className="signup-modal__summary-row">
                <dt>Klass</dt>
                <dd>
                  {form.className.toUpperCase()} · {students} elever
                </dd>
              </div>
            </dl>
            {submitError && (
              <p
                className="breaks-field__hint breaks-field__hint--error mb-0"
                role="alert"
              >
                {submitError}
              </p>
            )}
            {duplicateWarning && (
              <div className="notice" role="status">
                <p className="mb-0">
                  <strong>
                    {form.className.toUpperCase()} på {form.school} är redan
                    anmäld.
                  </strong>{" "}
                  Om en kollega har anmält klassen kan ni dela på lärarlänken i
                  stället, annars räknas klassen två gånger. Är det en annan
                  klass med samma namn kan du anmäla ändå.
                </p>
              </div>
            )}
            <div className="signup-modal__actions">
              <Button variant="link" onClick={backToClass}>
                Tillbaka
              </Button>
              <Button
                onClick={() => submit(duplicateWarning)}
                disabled={submitting}
                aria-busy={submitting}
              >
                {submitting
                  ? "Anmäler…"
                  : duplicateWarning
                    ? "Anmäl ändå"
                    : "Anmäl klassen"}
              </Button>
            </div>
          </ModalStep>
        )}

        {step === 4 && (
          <>
            <span className="breaks-graphic__pause" aria-hidden="true">
              <span />
              <span />
            </span>
            <ModalStep
              title={`Grattis! ${registration?.className ?? "Klassen"} är anmäld!`}
            >
              <p className="mb-0">
                {registration?.emailSent === false
                  ? "Anmälan är registrerad, men mejlet med er lärarlänk kunde inte skickas just nu. Be om en ny länk på startsidan om en stund."
                  : `Vi har mejlat ${registration?.email} bekräftelse, information och klassens egna länkar. Klasslänken kan du dela med klassen och föräldrarna. Där kan ni följa statistik på hur många skolor, klasser och elever som deltar.`}
              </p>
              {link !== "" && (
                <div className="class-link">
                  <p className="breaks-eyebrow mb-0">Er klasslänk</p>
                  <div className="class-link__row">
                    <span className="class-link__url">{link}</span>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={copy}
                      aria-label="Kopiera klasslänk"
                    >
                      {copied ? "Kopierad" : "Kopiera"}
                    </button>
                  </div>
                </div>
              )}
              {(link !== "" || videoLink !== "") && (
                <div className="d-flex flex-wrap gap-3">
                  {link !== "" && (
                    <a className="btn btn-primary" href={link}>
                      Till klassidan
                    </a>
                  )}
                  {videoLink !== "" && (
                    <a className="btn btn-outline-primary" href={videoLink}>
                      Till rörelserna
                    </a>
                  )}
                </div>
              )}
              <span
                className="visually-hidden"
                role="status"
                aria-live="polite"
              >
                {copied ? "Klasslänken kopierades." : ""}
              </span>
            </ModalStep>
          </>
        )}
      </div>
    </ModalShell>
  );
}
